<template>
  <!-- ONE CompanySite -- for INTERNAL users and SUPPLIERS (Oct 2026) -- give it a siteId, it loads everything else
       ONE card: the header, then the data sections -- VIEW mode by default; each section's Edit opens that section's form inline -->
  <b-overlay
    :show="loading"
    class="company-site-detail"
  >
    <div
      v-if="!site"
      class="text-muted"
    >
      {{ loading ? '' : $t('companySiteDetail.notFound') }}
    </div>
    <template v-else>
      <b-card
        class="shadow-sm"
        no-body
      >
        <div class="p-3">
          <!-- HEADER: name, state, actions (the CompanySite transitions this user may do + internal-only extras) -->
          <div class="d-flex flex-wrap align-items-center gap-2">
            <span class="mb-0 font-weight-600 font-size-130">{{ site.display_name }}</span>
            <b-badge
              :variant="stateParams.variant"
              class="text-uppercase"
            >{{ stateParams.label }}</b-badge>
            <b-badge
              v-if="site.is_primary"
              variant="primary"
              class="text-uppercase"
            >{{ $t('companySiteDetail.primarySite') }}</b-badge>
            <div
              v-if="!readOnly"
              class="ml-auto d-flex flex-wrap align-items-center justify-content-end gap-2"
            >
              <b-button
                v-for="action in headerActions"
                :key="action.trigger"
                size="sm"
                :variant="action.variant"
                :disabled="!!editingCard"
                @click="openAction(action)"
              >{{ action.label }}</b-button>
              <b-button
                v-if="canMakePrimary"
                size="sm"
                variant="gray-primary"
                :disabled="!!editingCard"
                @click="onMakePrimary"
              >{{ $t('companySiteDetail.makePrimary') }}</b-button>
              <b-button
                v-if="isInternal && allowDelete"
                variant="gray-danger"
                size="sm"
                :disabled="!!editingCard"
                @click="onDeleteSite"
              >
                <b-icon
                  icon="trash"
                  class="mr-1"
                ></b-icon>
                {{ $t('companySiteDetail.deleteSite') }}
              </b-button>
            </div>
          </div>

          <hr class="my-3" style="border-top-width: 3px;" />

          <!-- the current user's open reviews on this site (a new site from the supplier, or a supplier change request) -->
          <company-site-review-tasks
            v-if="!readOnly && site.my_review_tasks && site.my_review_tasks.length"
            :site-id="site.id"
            :tasks="site.my_review_tasks"
            class="mb-3"
            @review-done="refreshAll"
          ></company-site-review-tasks>

          <!-- what is still missing (vendors.services.sites.get_site_problems) -->
          <b-alert
            :show="(site.problems || []).length > 0"
            variant="warning"
            class="mb-3"
          >
            <div class="font-weight-bold mb-1">{{ $t('companySiteDetail.stillNeeded') }}</div>
            <ul class="mb-0 pl-4">
              <li
                v-for="(problem, idx) in site.problems"
                :key="idx"
              >{{ problem }}</li>
            </ul>
          </b-alert>

          <!-- a supplier change request waiting for approval -- the requested values show next to their fields below -->
          <b-alert
            :show="!!pendingChange"
            variant="info"
            class="mb-3"
          >
            <div class="font-weight-bold">{{ $t('companySiteDetail.pendingChanges') }}</div>
            <!-- <div v-if="pendingChange && pendingChange.requested_by_name">
              {{ $t('companySiteDetail.requestedBy', { name: pendingChange.requested_by_name }) }}
            </div> -->
            <div v-if="Object.keys(pendingFields).length">{{ $t('companySiteDetail.pendingMarkedBelow') }}</div>
            <div
              v-if="pendingChange && pendingChange.requested_state"
              class="font-weight-bold"
            >
              {{ pendingChange.requested_state === 'inactive' ? $t('companySiteDetail.requestedDeactivate') : $t('companySiteDetail.requestedRemove') }}
            </div>
          </b-alert>

          <!-- SITE TYPES -- its own form ('edit_site_types') -- will become a custom component (site type badges) -->
          <section class="company-site-detail__section">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 font-weight-bold text-primary">{{ $t('companySiteDetail.siteTypes') }}</h6>
              <b-button
                v-if="!editing.siteTypes && canTrigger('edit_site_types')"
                size="sm"
                variant="gray-primary"
                :disabled="isOtherCardEditing('siteTypes')"
                @click="startEditing('siteTypes')"
              >
                <b-icon
                  icon="pencil"
                  class="mr-2"
                ></b-icon>
                {{ $t('companySiteDetail.edit') }}
              </b-button>
              <b-button
                v-else-if="editing.siteTypes"
                size="sm"
                variant="secondary"
                @click="cancelEditing('siteTypes')"
              >
                {{ $t('buttons.cancel') }}
              </b-button>
            </div>
          <!-- VIEW -->
          <div v-if="!editing.siteTypes">
            <div
              v-if="site.site_types.length"
              class="d-flex flex-wrap gap-3 align-items-center"
            >
              <company-site-type-badge
                v-for="siteType in site.site_types"
                :key="siteType.id"
                :site-type="siteType"
              ></company-site-type-badge>
            </div>
            <span
              v-else
              class="text-muted"
            >&mdash;</span>
            <div
              v-if="pendingFields.site_types"
              class="company-site-detail__pending"
            >
              <b-badge variant="soft-warning">{{ $t('companySiteDetail.waitingForApproval') }}</b-badge>
              <span class="text-muted">{{ $t('companySiteDetail.requested') }}</span>
              <span>{{ pendingFields.site_types.requested }}</span>
            </div>
          </div>
          <!-- EDIT -->
          <FlowObjectTransitionForm
            v-else
            :flowObject="flowObject"
            trigger="edit_site_types"
            :hideCancel="true"
            :autoStart="true"
            :noReloadOnSuccess="true"
            @transition-success="afterCardSaved('siteTypes')"
          ></FlowObjectTransitionForm>
          </section>

          <!-- ADDRESS -- its own form ('edit_address') -- the same Address for every tenant -->
          <section class="company-site-detail__section">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 font-weight-bold text-primary">{{ $t('companySiteDetail.address') }}</h6>
              <b-button
                v-if="!editing.address && canTrigger('edit_address')"
                size="sm"
                variant="gray-primary"
                :disabled="isOtherCardEditing('address')"
                @click="startEditing('address')"
              >
                <b-icon
                  icon="pencil"
                  class="mr-2"
                ></b-icon>
                {{ $t('companySiteDetail.edit') }}
              </b-button>
              <b-button
                v-else-if="editing.address"
                size="sm"
                variant="secondary"
                @click="cancelEditing('address')"
              >
                {{ $t('buttons.cancel') }}
              </b-button>
            </div>
          <!-- VIEW -->
          <div v-if="!editing.address">
            <!-- written out like a postal address: street lines, then city / state / postcode, then the country -->
            <address
              v-if="addressLines.length || (site.address && (site.address.country_name || site.address.country_code))"
              class="mb-0"
            >
              <div
                v-for="(line, idx) in addressLines"
                :key="idx"
              >{{ line }}</div>
              <div
                v-if="site.address.country_name || site.address.country_code"
                class="d-flex align-items-center gap-2"
              >
                <span>{{ site.address.country_name || site.address.country_code }}</span>
                <country-flag
                  v-if="site.address.country_code"
                  :country="site.address.country_code"
                  size="sm"
                ></country-flag>
              </div>
            </address>
            <span
              v-else
              class="text-muted"
            >&mdash;</span>
            <div
              v-if="pendingFields.address"
              class="company-site-detail__pending"
            >
              <b-badge variant="soft-warning">{{ $t('companySiteDetail.waitingForApproval') }}</b-badge>
              <span class="text-muted">{{ $t('companySiteDetail.requested') }}</span>
              <span>{{ pendingFields.address.requested }}</span>
            </div>
          </div>
          <!-- EDIT -->
          <FlowObjectTransitionForm
            v-else
            :flowObject="flowObject"
            trigger="edit_address"
            :hideCancel="true"
            :autoStart="true"
            :noReloadOnSuccess="true"
            @transition-success="afterCardSaved('address')"
          ></FlowObjectTransitionForm>
          </section>

          <!-- DETAILS -- name, notes + the custom fields this user may SEE (read-only ones are only shown here) -- 'edit_site' form
               (the tenant's field access decides which fields are in each form) -->
          <section class="company-site-detail__section">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 font-weight-bold text-primary">{{ $t('companySiteDetail.siteDetails') }}</h6>
              <b-button
                v-if="!editing.details && canTrigger('edit_site')"
                size="sm"
                variant="gray-primary"
                :disabled="isOtherCardEditing('details')"
                @click="startEditing('details')"
              >
                <b-icon
                  icon="pencil"
                  class="mr-2"
                ></b-icon>
                {{ $t('companySiteDetail.edit') }}
              </b-button>
              <b-button
                v-else-if="editing.details"
                size="sm"
                variant="secondary"
                @click="cancelEditing('details')"
              >
                {{ $t('buttons.cancel') }}
              </b-button>
            </div>
          <!-- VIEW -->
          <dl
            v-if="!editing.details"
            class="row mb-0"
          >
            <dt class="col-sm-3">{{ $t('companySiteDetail.siteName') }}</dt>
            <dd class="col-sm-9">
              {{ site.name || site.display_name }}
              <div
                v-if="pendingFields.name"
                class="company-site-detail__pending"
              >
                <b-badge variant="soft-warning">{{ $t('companySiteDetail.waitingForApproval') }}</b-badge>
                <span class="text-muted">{{ $t('companySiteDetail.requested') }}</span>
                <span>{{ pendingFields.name.requested }}</span>
              </div>
            </dd>
            <template v-if="'notes' in site">
              <dt class="col-sm-3">{{ $t('companySiteDetail.notes') }}</dt>
              <dd class="col-sm-9 company-site-detail__pre-line">
                <span v-if="site.notes">{{ site.notes }}</span>
                <span
                  v-else
                  class="text-muted"
                >&mdash;</span>
                <div
                  v-if="pendingFields.notes"
                  class="company-site-detail__pending"
                >
                  <b-badge variant="soft-warning">{{ $t('companySiteDetail.waitingForApproval') }}</b-badge>
                  <span class="text-muted">{{ $t('companySiteDetail.requested') }}</span>
                  <span>{{ pendingFields.notes.requested }}</span>
                </div>
              </dd>
            </template>
            <!-- the custom fields with a value, or a requested change (empty ones are hidden until you edit) -->
            <template v-for="field in shownCustomFields">
              <dt
                :key="`label-${field.name}`"
                class="col-sm-3"
              >{{ field.label }}</dt>
              <dd
                :key="`value-${field.name}`"
                class="col-sm-9"
              >
                <span v-if="field.value !== ''">{{ field.value }}</span>
                <span
                  v-else
                  class="text-muted"
                >&mdash;</span>
                <div
                  v-if="pendingFields[field.name]"
                  class="company-site-detail__pending"
                >
                  <b-badge variant="soft-warning">{{ $t('companySiteDetail.waitingForApproval') }}</b-badge>
                  <span class="text-muted">{{ $t('companySiteDetail.requested') }}</span>
                  <span>{{ pendingFields[field.name].requested }}</span>
                </div>
              </dd>
            </template>
          </dl>
          <!-- EDIT -->
          <FlowObjectTransitionForm
            v-else
            :flowObject="flowObject"
            trigger="edit_site"
            :hideCancel="true"
            :autoStart="true"
            :noReloadOnSuccess="true"
            @transition-success="afterCardSaved('details')"
          ></FlowObjectTransitionForm>
          </section>

          <!-- CONTACTS -- ObjectTenantUsers by ObjectRole -- edited with the 'set_site_contacts' transition form
               (one picker per site role, with the tenant's min/max for this site's types) -->
          <section class="company-site-detail__section">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 font-weight-bold text-primary">{{ $t('companySiteDetail.siteContacts') }}</h6>
              <b-button
                v-if="!editing.contacts && canTrigger('set_site_contacts') && shownRoles.length"
                size="sm"
                variant="gray-primary"
                :disabled="isOtherCardEditing('contacts')"
                @click="startEditing('contacts')"
              >
                <b-icon
                  icon="pencil"
                  class="mr-2"
                ></b-icon>
                {{ $t('companySiteDetail.edit') }}
              </b-button>
              <b-button
                v-else-if="editing.contacts"
                size="sm"
                variant="secondary"
                @click="cancelEditing('contacts')"
              >
                {{ $t('buttons.cancel') }}
              </b-button>
            </div>
          <div
            v-if="!shownRoles.length"
            class="text-muted"
          >
            {{ $t('companySiteDetail.noRoles') }}
          </div>
          <!-- VIEW -->
          <dl
            v-else-if="!editing.contacts"
            class="row mb-0"
          >
            <template v-for="role in shownRoles">
              <dt
                :key="`role-${role.id}`"
                class="col-sm-3"
              >{{ role.name }}</dt>
              <dd
                :key="`tus-${role.id}`"
                class="col-sm-9"
              >
                <span v-if="(contactNamesByRoleId[role.id] || []).length">{{ contactNamesByRoleId[role.id].join(', ') }}</span>
                <span
                  v-else
                  class="text-muted"
                >&mdash;</span>
              </dd>
            </template>
          </dl>
          <!-- EDIT -->
          <FlowObjectTransitionForm
            v-else
            :flowObject="flowObject"
            trigger="set_site_contacts"
            :hideCancel="true"
            :autoStart="true"
            :noReloadOnSuccess="true"
            @transition-success="afterCardSaved('contacts')"
          ></FlowObjectTransitionForm>
          </section>

          <!-- TAGS (SplTags) -- internal users AND suppliers: SplTags decides which tag fields each user can see / edit (the site API
               only sends the visible ones) -- hidden when there are none -- TagFields shows them read-only, or editable when isEditing -->
          <section
            v-if="hasTagFields"
            class="company-site-detail__section"
          >
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 font-weight-bold text-primary">{{ $t('companySiteDetail.tags') }}</h6>
              <b-button
                v-if="!editing.tags && !readOnly"
                size="sm"
                variant="gray-primary"
                :disabled="isOtherCardEditing('tags')"
                @click="startEditing('tags')"
              >
                <b-icon
                  icon="pencil"
                  class="mr-2"
                ></b-icon>
                {{ $t('companySiteDetail.edit') }}
              </b-button>
              <b-button
                v-else-if="editing.tags"
                size="sm"
                variant="secondary"
                @click="cancelEditing('tags')"
              >
                {{ $t('buttons.cancel') }}
              </b-button>
            </div>
          <TagFields
            v-model="tagsValue"
            :title="$t('companySiteDetail.tags')"
            :config="tagFieldsConfig"
            :isEditing="editing.tags"
          ></TagFields>
          <b-button
            v-if="editing.tags && hasTagFields"
            variant="primary"
            class="mt-3"
            :disabled="savingTags"
            @click="onSaveTags"
          >
            <b-spinner
              v-if="savingTags"
              small
              class="mr-1"
            ></b-spinner>
            {{ $t('companySiteDetail.saveTags') }}
          </b-button>
          </section>

        </div>
      </b-card>

      <!-- STATE ACTIONS (approve, deactivate, request removal, ...) -- confirmation / form in a modal -->
      <b-modal
        v-model="actionModalShowing"
        :title="activeAction ? activeAction.label : ''"
        size="md"
        centered
        hide-footer
        no-close-on-backdrop
        @hidden="activeAction = null"
      >
        <FlowObjectTransitionForm
          v-if="activeAction"
          :key="activeAction.key"
          :flowObject="flowObject"
          :trigger="activeAction.trigger"
          :autoStart="true"
          :noReloadOnSuccess="true"
          min-height="200px"
          @transition-success="onActionSuccess"
          @cancel="actionModalShowing = false"
        ></FlowObjectTransitionForm>
      </b-modal>
    </template>
  </b-overlay>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep';
  import { suppliosApi } from '@/js/api-service';
  import { currentUserInfoMixin, errorHandling } from '@/js/custom-vue-mixins';
  import TagFields from '@/components/taxonomy/taggings/TagFields.vue';
  import FlowObjectTransitionForm from '@/components/flows/components/FlowObjectTransitionForm.vue';
  import { FlowObject } from '@/components/flows/models/flowObject';
  import CompanySiteTypeBadge from '@/components/vendors/components/CompanySiteTypeBadge.vue';
  import CompanySiteReviewTasks from '@/components/vendors/components/CompanySiteReviewTasks.vue';

  // ONE CompanySite -- for INTERNAL users AND SUPPLIERS (Oct 2026) -- used by CompanySitesManager (vendor page Sites tab) and
  // SharedCompanySitesManager (supplier portal, supplier onboarding tasks)
  // every change goes through the CompanySite WORKFLOW (companySiteFlow transitions), so the tenant's field access rules and
  // approvals apply to everyone in the same way (vendors/site_custom_data.py, vendors/services/site_changes.py):
  // - site types / address / details (name, notes, custom fields) -- one card + transition form each: 'edit_site_types',
  //   'edit_address', 'edit_site' (built from the tenant's field access)
  // - contacts (ObjectTenantUsers by site ObjectRole) -- 'set_site_contacts' transition form
  // - state changes -- the other transitions this user may do (approve, deactivate, request removal, withdraw, ...)
  // - tags (SplTags) -- for everyone who can see any site tag fields (SplTags decides what each user can see / edit)
  // INTERNAL-only extras: make primary, hard delete (the internal site API)
  // what the user may SEE comes from the site API (CompanySiteReadSerializer filters it per user)
  // EMITS: 'saved' (fresh site data) after any change, 'deleted' (site id) after a delete / when the site was removed
  // ONLY for tenants with custom_flags.use_vendor_sites

  // the transitions that have their own card (not header buttons)
  const CARD_TRIGGERS = ['edit_site', 'edit_site_types', 'edit_address', 'set_site_contacts', 'update_custom_fields'];
  const ACTION_VARIANTS = {
    approve_site: 'success',
    reject_site: 'gray-danger',
    withdraw_new_site: 'gray-danger',
    request_deactivate_site: 'gray-secondary',
    request_remove_site: 'gray-danger',
    deactivate_site: 'gray-secondary',
    reactivate_site: 'gray-success',
    remove_site: 'gray-danger',
  };

  export default {
    name: 'CompanySiteDetail',
    components: {
      TagFields,
      FlowObjectTransitionForm,
      CompanySiteTypeBadge,
      CompanySiteReviewTasks,
    },
    mixins: [errorHandling, currentUserInfoMixin],
    i18n: {
      // English only for now -- other languages fall back to English
      messages: {
        en: {
          companySiteDetail: {
            notFound: 'Site not found.',
            primarySite: 'Primary site',
            makePrimary: 'Make Primary',
            edit: 'Edit',
            deleteSite: 'Delete Site',
            deleteConfirm: 'Delete this site? This cannot be undone.',
            siteDetails: 'Site Details',
            siteName: 'Site Name',
            siteTypes: 'Site Types',
            address: 'Address',
            notes: 'Notes',
            siteContacts: 'Site Contacts',
            noRoles: 'No site roles are set up for this account yet.',
            tags: 'Classifications',
            saveTags: 'Save Classifications',
            stillNeeded: 'Still needed for this site:',
            pendingChanges: 'Changes waiting for approval',
            requestedBy: 'Requested by {name}',
            pendingMarkedBelow: 'The requested values are shown with each field below.',
            requestedDeactivate: 'Requested: deactivate this site',
            requestedRemove: 'Requested: remove this site',
            waitingForApproval: 'Waiting for approval',
            requested: 'Requested:',
            savedWaiting: 'Saved. Waiting for approval: {fields}',
            stateNew: 'New - pending approval',
            stateActive: 'Active',
            stateInactive: 'Inactive',
            stateRemoved: 'Removed',
            saved: 'Saved',
            errorLoading: 'Error while loading the site',
            errorSaving: 'Error while saving the site',
            errorDeleting: 'Error while deleting the site',
          },
        },
      },
    },
    props: {
      siteId: {
        type: String,
        required: true,
      },
      allowDelete: {
        // INTERNAL users: show the Delete Site button
        type: Boolean,
        default: true,
      },
      readOnly: {
        // just show the site -- no edits / actions
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loading: false,
        savingTags: false,
        editing: { siteTypes: false, address: false, details: false, contacts: false, tags: false }, // which card is in edit mode -- at most ONE at a time
        site: null, // CompanySiteReadSerializer data (already filtered to what this user may see)
        flowObject: null, // FlowObject for the site -- transitions, object roles, contacts
        transitions: [], // the CompanySite transitions this user may do now
        objectRoles: [], // the ObjectRoles on CompanySite for this tenant
        objectTus: [], // the site's contacts (ObjectTenantUsers)
        tagsValue: {}, // { tagFieldId: [tag, ...] } -- v-model for TagFields
        actionModalShowing: false,
        activeAction: null, // { key, trigger, label }
      };
    },
    computed: {
      isInternal() {
        return !!this.currentUserInfo?.is_internal;
      },
      stateParams() {
        const params = {
          new: { label: this.$t('companySiteDetail.stateNew'), variant: 'soft-info' },
          active: { label: this.$t('companySiteDetail.stateActive'), variant: 'soft-success' },
          inactive: { label: this.$t('companySiteDetail.stateInactive'), variant: 'soft-secondary' },
          removed: { label: this.$t('companySiteDetail.stateRemoved'), variant: 'soft-danger' },
        };
        return params[this.site?.sm_state] || { label: this.site?.sm_state || '', variant: 'soft-secondary' };
      },
      triggers() {
        return this.transitions.map((trans) => trans.trigger);
      },
      headerActions() {
        return this.transitions
          .filter((trans) => !CARD_TRIGGERS.includes(trans.trigger))
          .map((trans) => ({ trigger: trans.trigger, label: trans.label || trans.trigger, variant: ACTION_VARIANTS[trans.trigger] || 'outline-secondary' }));
      },
      canMakePrimary() {
        return this.isInternal && this.site && !this.site.is_primary && this.site.sm_state === 'active';
      },
      addressLines() {
        // the address (without the country -- shown separately, with its flag) as postal lines:
        // '123 Main St, Suite 4' / addr2 / addr3 / 'City, State Postcode' -- falls back to the raw address if nothing is parsed
        const address = this.site?.address;
        if (!address) return [];
        const street = [address.addr1, address.subpremise].filter(Boolean).join(', ');
        const cityLine = [[address.city, address.state].filter(Boolean).join(', '), address.postcode].filter(Boolean).join(' ');
        const lines = [street, address.addr2, address.addr3, cityLine].filter(Boolean);
        if (!lines.length && address.raw_address) return [address.raw_address];
        return lines;
      },
      pendingChange() {
        // the supplier change request waiting for approval, or null -- { requested_at, requested_by_name, requested_state, last_held_back, fields }
        return this.site?.pending_change || null;
      },
      pendingFields() {
        // { field key: { label, saved, requested } } -- display strings -- keys: name / notes / address / site_types / custom field names
        return this.pendingChange?.fields || {};
      },
      shownCustomFields() {
        // view mode hides empty custom fields ('' / null come back as an empty display value) -- unless a change to them is waiting
        return (this.site?.custom_field_values || [])
          .map((field) => ({ ...field, value: field.value ?? '' }))
          .filter((field) => field.value !== '' || this.pendingFields[field.name]);
      },
      shownRoles() {
        // suppliers only see the roles their contacts can hold
        return this.isInternal ? this.objectRoles : this.objectRoles.filter((role) => role.allow_any_external);
      },
      contactNamesByRoleId() {
        const byRole = {};
        this.objectTus.forEach((otu) => {
          const roleId = otu.role?.id ?? otu.role_id;
          byRole[roleId] = byRole[roleId] || [];
          byRole[roleId].push(otu.tenant_user?.full_name || otu.tenant_user?.email || '');
        });
        return byRole;
      },
      tagFieldsConfig() {
        // useInternalEdit=false: no Edit/Save header in TagFields -- edit mode comes from isEditing, and we save with onSaveTags()
        return { modelLabel: 'vendors.CompanySite', objectId: this.siteId, layout: 'vertical', useInternalEdit: false };
      },
      hasTagFields() {
        // the keys are the tag fields this user can see -- taggings_by_field has every visible field, even ones with no tags yet
        return Object.keys(this.tagsValue || {}).length > 0;
      },
      editingCard() {
        return Object.keys(this.editing).find((card) => this.editing[card]) || null;
      },
    },
    watch: {
      siteId() {
        this.load();
      },
    },
    mounted() {
      this.load();
    },
    methods: {
      async load() {
        this.stopAllEditing(); // never carry unsaved edits to another site
        this.loading = true;
        try {
          this.flowObject = new FlowObject({ id: this.siteId, model_class: 'CompanySite', data: {} });
          await this.fetchEverything();
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteDetail.errorLoading'));
        } finally {
          this.loading = false;
        }
      },
      async fetchEverything() {
        const [siteResponse, transitions, objectRoles, objectTus] = await Promise.all([
          suppliosApi.companySite.retrieve(this.siteId),
          this.readOnly ? Promise.resolve([]) : this.flowObject.fetchTransitions(),
          this.flowObject.getObjectRoles({ refresh: true }),
          this.flowObject.getOtus({ refresh: true }),
        ]);
        this.site = siteResponse.data;
        this.transitions = transitions || [];
        this.objectRoles = objectRoles || [];
        this.objectTus = objectTus || [];
        if (!this.editing.tags) this.tagsValue = cloneDeep(this.site?.taggings_by_field || {});
      },
      async refreshAll() {
        // after any change -- reload what the backend has now (state, pending changes, problems, contacts, available actions)
        await this.fetchEverything();
        if (this.site?.sm_state === 'removed') {
          this.$emit('deleted', this.siteId); // removed sites drop out of the site lists
          return;
        }
        this.$emit('saved', this.site);
      },
      canTrigger(trigger) {
        return !this.readOnly && this.triggers.includes(trigger);
      },
      isOtherCardEditing(card) {
        return !!this.editingCard && this.editingCard !== card;
      },
      startEditing(card) {
        if (this.isOtherCardEditing(card)) return;
        if (card === 'tags') this.tagsValue = cloneDeep(this.site?.taggings_by_field || {});
        this.editing[card] = true; // details / contacts: the transition form loads its own current values when it starts
      },
      cancelEditing(card) {
        this.editing[card] = false;
        if (card === 'tags') this.tagsValue = cloneDeep(this.site?.taggings_by_field || {});
      },
      stopAllEditing() {
        Object.keys(this.editing).forEach((card) => {
          this.editing[card] = false;
        });
      },
      async afterCardSaved(card) {
        this.editing[card] = false;
        const requestedAtBefore = this.pendingChange?.requested_at || null;
        await this.refreshAll();
        // the backend says which fields THIS save kept back for approval (a new / updated change request)
        const heldBack = this.pendingChange && this.pendingChange.requested_at !== requestedAtBefore ? this.pendingChange.last_held_back || [] : [];
        if (heldBack.length) {
          this.$toast.info(this.$t('companySiteDetail.savedWaiting', { fields: heldBack.join(', ') }));
        } else {
          this.$toast.success(this.$t('companySiteDetail.saved'));
        }
      },
      openAction(action) {
        this.activeAction = { ...action, key: `${action.trigger}-${Date.now()}` };
        this.actionModalShowing = true;
      },
      async onActionSuccess() {
        this.actionModalShowing = false;
        await this.refreshAll();
      },
      async onMakePrimary() {
        try {
          await suppliosApi.companySite.setPrimary(this.siteId);
          await this.refreshAll();
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteDetail.errorSaving'));
        }
      },
      async onSaveTags() {
        this.savingTags = true;
        try {
          // same payload TagFields builds when it saves itself -- one entry per tag field, with exactly the tags to keep
          // the backend silently skips fields this user's role cannot edit
          const tagData = Object.entries(this.tagsValue).map(([tagFieldId, tags]) => ({
            object_id: this.siteId,
            tag_field_id: tagFieldId,
            tag_ids: (tags || []).map((tag) => tag.id),
          }));
          await suppliosApi.companySite.update_spl_taggings(this.siteId, tagData);
          await this.afterCardSaved('tags');
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteDetail.errorSaving'));
        } finally {
          this.savingTags = false;
        }
      },
      async onDeleteSite() {
        const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('companySiteDetail.deleteConfirm'), {
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('companySiteDetail.deleteSite'),
          cancelTitle: this.$t('buttons.cancel'),
          centered: true,
        });
        if (!confirmed) return;
        try {
          await suppliosApi.companySite.destroy(this.siteId);
          this.stopAllEditing();
          this.$emit('deleted', this.siteId);
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteDetail.errorDeleting'));
        }
      },
    },
  };
</script>

<style scoped>
  .company-site-detail {
    height: 100%;
  }
  .company-site-detail__pre-line {
    white-space: pre-line;
  }
  /* a requested change (waiting for approval) under the saved value */
  .company-site-detail__pending {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    white-space: normal;
  }
  /* the data sections inside the one card -- a light line between them */
  .company-site-detail__section + .company-site-detail__section {
    border-top: 1px solid var(--gray-200);
    margin-top: 1rem;
    padding-top: 1rem;
  }
</style>
